import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { effectivePrice, formatMoney, type StockProblem } from "@neus/shared";
import { api, checkoutProblems } from "../api/client";
import { useEffectivePrice, useMe, useProduct, useStock } from "../api/hooks";
import { ContractorTag, Price, QtyStepper, StockBadge } from "../components/product/bits";
import { features } from "../features";
import { MAX_QTY, toCartLines, useCart, type CartItem } from "../store/cart";
import styles from "./pages.module.css";

function CartRow({ item, problem }: { item: CartItem; problem?: StockProblem }) {
  const { data: product, isError } = useProduct(item.sku);
  const stock = useStock(product);
  const pricing = useEffectivePrice({ sku: item.sku, price: product?.price ?? item.price });
  const setQty = useCart((s) => s.setQty);
  const remove = useCart((s) => s.remove);
  const known = product !== undefined;
  const tooMany = known && item.qty > stock;
  const unavailable = isError || problem?.reason === "not_found";

  return (
    <li className={`${styles.cartRow} ${tooMany || unavailable ? styles.cartRowWarn : ""}`}>
      <Link to={`/products/${encodeURIComponent(item.sku)}`} className={styles.cartThumb}>
        {item.image && <img src={item.image} alt="" />}
      </Link>
      <div className={styles.cartInfo}>
        <span className="muted">{item.brand}</span>
        <Link to={`/products/${encodeURIComponent(item.sku)}`}>
          <strong>{item.name}</strong>
        </Link>
        <span className="muted">SKU {item.sku}</span>
        {pricing?.regularPrice !== undefined && <ContractorTag />}
        {known && <StockBadge stock={stock} />}
        {unavailable && <span className={styles.cartWarn}>This item is no longer available.</span>}
        {tooMany && !unavailable && (
          <span className={styles.cartWarn}>
            {stock === 0 ? "Now out of stock." : `Only ${stock} available. Please reduce the quantity.`}
          </span>
        )}
      </div>
      <div className={styles.cartQty}>
        <QtyStepper
          value={item.qty}
          max={Math.max(item.qty, Math.min(MAX_QTY, known ? stock : MAX_QTY))}
          onChange={(q) => setQty(item.sku, q)}
          label={`Quantity for ${item.name}`}
        />
        <button type="button" className={styles.linkButton} onClick={() => remove(item.sku)}>
          Remove
        </button>
      </div>
      <div className={styles.cartPrice}>
        <Price
          price={(pricing?.price ?? item.price) * item.qty}
          regularPrice={pricing?.regularPrice !== undefined ? pricing.regularPrice * item.qty : undefined}
        />
      </div>
    </li>
  );
}

/** With card payments and pay-in-store pickup both on, the cart offers both, each labeled by how it's paid. */
const BOTH = features.cardPayments && features.payInStorePickup;

const note = { fontSize: "0.8rem", textAlign: "center", marginTop: 10 } as const;

/** Pay by card online (Stripe Checkout); no account needed. */
function CheckoutOnline() {
  const navigate = useNavigate();
  return (
    <div>
      <p className="muted" style={{ fontSize: "0.85rem" }}>
        Shipping is calculated at checkout. Free shipping on qualifying orders.
      </p>
      <button type="button" className="btn btn--lg btn--block" onClick={() => navigate("/checkout")}>
        {BOTH ? "Checkout Online" : "Checkout"}
      </button>
      <p className="muted" style={note}>
        No account needed. Secure payment by Stripe.
      </p>
    </div>
  );
}

/**
 * Pay in store: send the cart to the store for pickup without paying (signed in only; signed out, the button goes
 * to sign-in first). Staff gather it and ring it up at the register when the shopper comes in.
 */
function CheckoutInStore({ signedIn }: { signedIn: boolean }) {
  const items = useCart((s) => s.items);
  const clear = useCart((s) => s.clear);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = BOTH ? "Checkout in-store" : "Checkout";

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const order = await api.createPickupOrder({ lines: toCartLines(items) });
      clear();
      void qc.invalidateQueries({ queryKey: ["my-orders"] });
      navigate("/account/orders", { state: { pickupOrder: order.id } });
    } catch (err) {
      const problems = checkoutProblems(err);
      if (problems) navigate("/cart", { replace: true, state: { problems } });
      else setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setBusy(false);
    }
  };
  return (
    <div style={BOTH ? { marginTop: 20 } : undefined}>
      {signedIn ? (
        <button type="button" className="btn btn--lg btn--block" disabled={busy} onClick={() => void send()}>
          {busy ? "Sending…" : label}
        </button>
      ) : (
        <Link to="/account/sign-in?next=%2Fcart" className="btn btn--lg btn--block">
          {label}
        </Link>
      )}
      <p className="muted" style={note}>
        Pick up in store, pay there: we gather your order, and you pay at the register when you pick it up.
        {!signedIn && " Sign in first."}
      </p>
      {error && (
        <div className="notice notice--warn" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export function CartPage() {
  const items = useCart((s) => s.items);
  const { data: me } = useMe();
  // The live product prices the rows show (cached by their queries), with contractor prices: what
  // the server will charge. The cart's stored price is only a fallback while a product loads.
  const live = useQueries({
    queries: items.map((i) => ({ queryKey: ["product", i.sku], queryFn: () => api.product(i.sku) })),
  });
  const subtotal = items.reduce((sum, i, n) => {
    const price = live[n]?.data?.price ?? i.price;
    return sum + effectivePrice({ sku: i.sku, price }, me).price * i.qty;
  }, 0);
  const location = useLocation();
  const problems = (location.state as { problems?: StockProblem[] } | null)?.problems ?? [];

  if (items.length === 0) {
    return (
      <div className="container section" style={{ textAlign: "center" }}>
        <h1>Your cart is empty</h1>
        <p>Find the right tool for the job.</p>
        <Link to="/products" className="btn btn--lg">
          Shop products
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="container">
          <h1>Your Cart</h1>
        </div>
      </div>
      <div className={`container section ${styles.cartLayout}`}>
        <div>
          {problems.length > 0 && (
            <div className="notice notice--warn" role="alert">
              Stock changed while you were shopping. Please review the highlighted items.
            </div>
          )}
          <ul className={styles.cartList}>
            {items.map((item) => (
              <CartRow key={item.sku} item={item} problem={problems.find((p) => p.sku === item.sku)} />
            ))}
          </ul>
          <Link to="/products">← Continue shopping</Link>
        </div>

        <aside className={styles.summary}>
          <h2>Order Summary</h2>
          <div className={styles.summaryRow}>
            <span>Subtotal</span>
            <strong>{formatMoney(subtotal)}</strong>
          </div>
          {features.cardPayments && <CheckoutOnline />}
          {features.payInStorePickup && <CheckoutInStore signedIn={Boolean(me)} />}
        </aside>
      </div>
    </>
  );
}
